import { useState, useEffect } from "react";
import { Star } from "@phosphor-icons/react";
import { useInView } from "@/hooks/useInView";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOOGLE_PROFILE_URL = "https://www.google.com/search?q=3sm+training+and+programming+dallas";

function ReviewCard({ t }) {
  const initial = t.name.charAt(0).toUpperCase();
  const color = "bg-[#D4AF37]";

  return (
    <div
      data-testid={`review-card-${t.id}`}
      className="flex-shrink-0 w-[320px] sm:w-[360px] glass-card border border-white/10 rounded-xl p-6 hover:border-[#D4AF37]/30 transition-all"
    >
      <div className="flex items-center gap-3 mb-4">
        <div className={`w-9 h-9 rounded-full ${color} flex items-center justify-center text-[#050505] font-heading font-bold text-sm`}>
          {initial}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[#F2EFE9] font-heading text-sm font-semibold truncate">{t.name}</p>
          <p className="text-[10px] text-[#8C8C8C] font-body">{t.role}</p>
        </div>
      </div>
      <div className="flex gap-0.5 mb-3">
        {Array.from({ length: t.rating }).map((_, i) => (
          <Star key={i} size={14} weight="fill" className="text-[#D4AF37]" />
        ))}
      </div>
      <p className="text-sm text-[#8C8C8C] font-body leading-relaxed line-clamp-4">{t.quote}</p>
    </div>
  );
}

export default function GoogleReviewsWidget() {
  const [reviews, setReviews] = useState([]);
  const [ref, inView] = useInView();
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    axios.get(`${API}/testimonials`).then(({ data }) => setReviews(data)).catch(() => {});
  }, []);

  if (!reviews.length) return null;

  return (
    <section id="testimonials" data-testid="testimonials-section" className="bg-[#111111] py-16 sm:py-20" ref={ref}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className={`flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-10 reveal ${inView ? 'visible' : ''}`}>
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#D4AF37] font-body mb-4">77 Five-Star Google Reviews</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-[#F2EFE9]">
              What clients say.
            </h2>
          </div>
          <div data-testid="google-rating-summary" className="flex items-center gap-4 glass-card border border-white/10 rounded-xl px-6 py-4">
            <div className="text-center">
              <p className="font-heading text-3xl font-black text-[#F2EFE9] leading-none">5.0</p>
            </div>
            <div className="border-l border-white/10 pl-4">
              <div className="flex gap-0.5 mb-1">
                {[1, 2, 3, 4, 5].map(i => <Star key={i} size={16} weight="fill" className="text-[#D4AF37]" />)}
              </div>
              <p className="text-xs text-[#8C8C8C] font-body">Based on <span className="text-[#F2EFE9] font-semibold">77</span> reviews</p>
            </div>
          </div>
        </div>
      </div>

      {/* CSS Marquee */}
      <div
        className="overflow-hidden"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onTouchStart={() => setPaused(true)}
        onTouchEnd={() => setTimeout(() => setPaused(false), 3000)}
      >
        <div
          className="flex gap-5 w-max"
          style={{
            animation: `marquee ${reviews.length * 8}s linear infinite`,
            animationPlayState: paused ? 'paused' : 'running',
          }}
        >
          {[...reviews, ...reviews, ...reviews].map((t, i) => (
            <ReviewCard key={`${t.id}-${i}`} t={t} />
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 mt-8">
        <a
          href={GOOGLE_PROFILE_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="see-all-reviews-link"
          className="inline-flex items-center gap-2 text-sm text-[#8C8C8C] font-body hover:text-[#D4AF37] transition-colors"
        >
          See all 77 five-star reviews on Google
          <span className="text-[#D4AF37]">&rarr;</span>
        </a>
      </div>

      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-33.333%); }
        }
      `}</style>
    </section>
  );
}
